import { isAutosCategory } from '@kijiji/category'
import { type UserType } from '@kijiji/generated/graphql-types'
import { ChevronIcon } from '@kijiji/icons/src/icons'
import { useTranslation } from 'next-i18next'
import { type FC } from 'react'
import { useTheme } from 'styled-components'

import { HideAtOrLarger, ShowAtOrLarger } from '@/components/shared/breakpoint'
import { ImageCustom } from '@/components/shared/image'
import { VipSectionContainer } from '@/components/vip/shared/VipSectionContainer'
import {
  VipFinancingSectionMobileChevron,
  VipFinancingSectionWrapper,
} from '@/components/vip/vip-financing/styled'
import { VipFinancingModal } from '@/components/vip/vip-financing/VipFinancingModal'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { MODAL_REGISTRY } from '@/features/modal/constants/modalRegistry'
import { useModal } from '@/features/modal/hooks/useModal'
import { isAutosDealerProfile } from '@/features/profile/utils/isAutosDealerProfile'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { type VipQueryListing } from '@/types/listings'
import { BodyText } from '@/ui/atoms/body-text'
import { Button } from '@/ui/atoms/button'
import { Flex } from '@/ui/atoms/flex'
import { HeadlineText } from '@/ui/atoms/headline-text'
import dealerFinancingImage from '~/public/next-assets/images/dealer-financing.png'

export type VipFinancingSectionProps = {
  categoryId: number
  externalSourceId?: string
  flags: VipQueryListing['flags']
  listingId: string
  userType?: UserType
}

export const VipFinancingSection: FC<VipFinancingSectionProps> = ({
  categoryId,
  externalSourceId,
  flags,
  listingId,
  userType,
}) => {
  const { handleOpenModal } = useModal(MODAL_REGISTRY.LISTING_FINANCING)

  const { t } = useTranslation(TRANSLATION_KEYS.LISTING)
  const { spacing } = useTheme()

  /**
   * The financing section should only be shown if the "financingAvailable" flag is true or if it's
   * a dealer listing in the Cars & Trucks category.
   */
  const availableOnNpv = 'financingAvailable' in flags ? flags.financingAvailable : false
  const availableOnPv = isAutosCategory(categoryId) && isAutosDealerProfile(userType)

  if (!availableOnNpv && !availableOnPv) return null

  const handleClick = () => {
    handleOpenModal()
    trackEvent({ action: GA_EVENT.AutoFinancingShow })
  }

  const financingContent = () => {
    return (
      <Flex gap={spacing.default} alignItems="center" justifyContent="space-between">
        <ImageCustom srcImage={dealerFinancingImage} alt="" skipNextImage />

        <Flex gap={spacing.mini} flexDirection="column">
          <HeadlineText size="medium" as="h2">
            {t('vip:financing.title')}
          </HeadlineText>
          <BodyText size="medium">{t('vip:financing.description')}</BodyText>
        </Flex>
      </Flex>
    )
  }

  return (
    <>
      <VipSectionContainer>
        <ShowAtOrLarger breakpoint="medium">
          <VipFinancingSectionWrapper>
            {financingContent()}

            <Button variant="subtle" onClick={handleClick}>
              {t('vip:dealer_request.inquiry_label')}
            </Button>
          </VipFinancingSectionWrapper>
        </ShowAtOrLarger>

        {/* The entire wrapper turns into a button on mobile*/}
        <HideAtOrLarger breakpoint="medium">
          <VipFinancingSectionWrapper
            as="button"
            aria-label={t('vip:dealer_request.inquiry_label')}
            onClick={handleClick}
          >
            {financingContent()}

            <VipFinancingSectionMobileChevron>
              <ChevronIcon aria-hidden />
            </VipFinancingSectionMobileChevron>
          </VipFinancingSectionWrapper>
        </HideAtOrLarger>
      </VipSectionContainer>

      <VipFinancingModal listingId={listingId} externalSourceId={externalSourceId} />
    </>
  )
}
